import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import API_BASE from '../../utils/api';
import { SkeletonStatCard } from '../../components/Skeleton';

const SuperAdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      // Fetch from admin attendance endpoint (super admin can access all data)
      const res = await axios.get(`${API_BASE}/attendance/dashboard-stats`);
      setOverview(res.data.overview);
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const o = overview || {};

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        {/* Header */}
        <nav className="bg-gradient-to-r from-red-600 to-red-700 shadow-lg sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white">👑 Super Admin Dashboard</h1>
              <p className="text-red-100 text-sm">{new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
            <div className="flex items-center gap-4">
              <button onClick={fetchDashboardStats} className="text-white hover:text-red-100 text-sm font-medium flex items-center gap-1">
                🔄 Refresh
              </button>
              <ProfileDropdown />
            </div>
          </div>
        </nav>

        <main className="p-6">
          {/* System Status Alert */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <span className="text-2xl">✓</span>
            <div>
              <p className="font-semibold text-green-800">System Operating Normally</p>
              <p className="text-sm text-green-700">All modules are running smoothly. Last sync: Just now</p>
            </div>
          </div>

          {/* Top Stats Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => <SkeletonStatCard key={i} />)
            ) : (
              <>
                <div className="bg-white rounded-lg shadow p-5 hover:shadow-lg transition border-l-4 border-blue-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-xs font-medium uppercase">Total Students</p>
                      <p className="text-3xl font-bold mt-1 text-blue-600">{o.totalStudents || 0}</p>
                    </div>
                    <div className="p-3 rounded-full bg-blue-100"><span className="text-2xl">👥</span></div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-5 hover:shadow-lg transition border-l-4 border-green-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-xs font-medium uppercase">Total Teachers</p>
                      <p className="text-3xl font-bold mt-1 text-green-600">{o.totalTeachers || 0}</p>
                    </div>
                    <div className="p-3 rounded-full bg-green-100"><span className="text-2xl">👨‍🏫</span></div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-5 hover:shadow-lg transition border-l-4 border-purple-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-xs font-medium uppercase">Total Classes</p>
                      <p className="text-3xl font-bold mt-1 text-purple-600">{o.totalClasses || 0}</p>
                    </div>
                    <div className="p-3 rounded-full bg-purple-100"><span className="text-2xl">🏫</span></div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-5 hover:shadow-lg transition border-l-4 border-orange-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-xs font-medium uppercase">Active Users</p>
                      <p className="text-3xl font-bold mt-1 text-orange-600">{(o.totalStudents || 0) + (o.totalTeachers || 0)}</p>
                    </div>
                    <div className="p-3 rounded-full bg-orange-100"><span className="text-2xl">👤</span></div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Management Sections */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                ⚡ Quick Actions
              </h2>
              <div className="space-y-2">
                <button
                  onClick={() => navigate('/admin/users')}
                  className="w-full text-left p-3 bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 rounded-lg transition border border-blue-200"
                >
                  <p className="font-semibold text-blue-900">👥 Manage All Users</p>
                  <p className="text-xs text-blue-700">Create, edit, or delete user accounts</p>
                </button>
                <button
                  onClick={() => navigate('/admin/students')}
                  className="w-full text-left p-3 bg-gradient-to-r from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 rounded-lg transition border border-green-200"
                >
                  <p className="font-semibold text-green-900">📚 View All Students</p>
                  <p className="text-xs text-green-700">Access complete student database</p>
                </button>
                <button
                  onClick={() => navigate('/admin/teachers')}
                  className="w-full text-left p-3 bg-gradient-to-r from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 rounded-lg transition border border-purple-200"
                >
                  <p className="font-semibold text-purple-900">👨‍🏫 Manage Teachers</p>
                  <p className="text-xs text-purple-700">Handle staff and employment records</p>
                </button>
                <button
                  onClick={() => navigate('/admin/settings')}
                  className="w-full text-left p-3 bg-gradient-to-r from-red-50 to-red-100 hover:from-red-100 hover:to-red-200 rounded-lg transition border border-red-200"
                >
                  <p className="font-semibold text-red-900">⚙️ System Settings</p>
                  <p className="text-xs text-red-700">Configure system-wide parameters</p>
                </button>
              </div>
            </div>

            {/* System Info */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                ℹ️ System Information
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center pb-3 border-b">
                  <span className="text-gray-600">Current Role</span>
                  <span className="font-semibold text-red-600">👑 Super Administrator</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b">
                  <span className="text-gray-600">User</span>
                  <span className="font-semibold text-gray-800">{user?.name}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b">
                  <span className="text-gray-600">Email</span>
                  <span className="font-semibold text-gray-800">{user?.email}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b">
                  <span className="text-gray-600">Access Level</span>
                  <span className="font-semibold text-green-600">🔓 Unrestricted</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Modules</span>
                  <span className="font-semibold text-gray-800">All (+20)</span>
                </div>
              </div>
            </div>
          </div>

          {/* Permissions Overview */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              🔐 Access Overview
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { name: 'Students', icon: '👥', access: '✓' },
                { name: 'Teachers', icon: '👨‍🏫', access: '✓' },
                { name: 'Attendance', icon: '📋', access: '✓' },
                { name: 'Fees', icon: '💰', access: '✓' },
                { name: 'Exams', icon: '📝', access: '✓' },
                { name: 'Salary', icon: '💵', access: '✓' },
                { name: 'Leaves', icon: '📅', access: '✓' },
                { name: 'Transport', icon: '🚌', access: '✓' },
                { name: 'Classes', icon: '🏫', access: '✓' },
                { name: 'Settings', icon: '⚙️', access: '✓' },
                { name: 'Reports', icon: '📈', access: '✓' },
                { name: 'Users', icon: '👤', access: '✓' },
              ].map((module, idx) => (
                <div key={idx} className="bg-gradient-to-br from-green-50 to-green-100 p-3 rounded-lg border border-green-200 text-center">
                  <p className="text-2xl mb-1">{module.icon}</p>
                  <p className="text-xs font-semibold text-gray-800">{module.name}</p>
                  <p className="text-xl text-green-600 font-bold">{module.access}</p>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
